import { useState, useEffect, useCallback } from 'react'
import { Routes, Route, useNavigate, useParams } from 'react-router-dom'
import { Layout } from './components/Layout'
import { DocumentUpload } from './components/DocumentUpload'
import { DocumentList } from './components/DocumentList'
import { StudentList } from './components/StudentList'
import { StudentDetail } from './components/StudentDetail'
import { StudentCreate } from './components/StudentCreate'
import { SmartAnalysisView } from './components/SmartAnalysisView'
import LandingPage from './components/LandingPage'
import AuthModal from './components/AuthModal'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { Document, Student } from './types'
import { getDocuments, getDocument, getStudents, getStudent } from './utils/api'

// Home page component
const HomePage = () => {
  const [documents, setDocuments] = useState<Document[]>([])
  const [students, setStudents] = useState<Student[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null) // Clear any previous errors
      
      // Load both documents and students in parallel
      const [docs, studentList] = await Promise.all([
        getDocuments(),
        getStudents()
      ])
      
      setDocuments(docs)
      setStudents(studentList)
    } catch (error: any) {
      console.error('Failed to load data:', error)
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to load data. Please try again.'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const handleSelectDocument = (document: Document) => {
    navigate(`/document/${document.id}`)
  }

  const handleSelectStudent = (student: Student) => {
    navigate(`/student/${student.id}`)
  }

  const handleUploadNew = () => {
    navigate('/upload')
  }

  const handleCreateStudent = () => {
    navigate('/students/create')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Students Section */}
      <div>
        <StudentList
          students={students}
          error={error}
          onSelectStudent={handleSelectStudent}
          onCreateNew={handleCreateStudent}
          onStudentUpdated={loadData}
          onRetry={loadData}
        />
      </div>
      
      {/* Documents Section */}
      <div>
        <DocumentList
          documents={documents}
          onSelectDocument={handleSelectDocument}
          onUploadNew={handleUploadNew}
          onDocumentUpdated={loadData}
          showStudentInfo={true}
        />
      </div>
    </div>
  )
}

// Upload page component
const UploadPage = () => {
  const [documents, setDocuments] = useState<Document[]>([])
  const navigate = useNavigate()

  const loadDocumentCount = async () => {
    try {
      const docs = await getDocuments()
      setDocuments(docs)
    } catch (error) {
      console.error('Failed to load documents:', error)
    }
  }

  useEffect(() => {
    loadDocumentCount()
  }, [])

  const handleUploadSuccess = (document: Document) => {
    navigate(`/document/${document.id}`)
  }

  const handleViewDocuments = () => {
    navigate('/')
  }

  return (
    <DocumentUpload
      onUploadSuccess={handleUploadSuccess}
      onViewDocuments={handleViewDocuments}
      documentCount={documents.length}
    />
  )
}

// Document analysis page component
const DocumentPage = () => {
  const { documentId } = useParams<{ documentId: string }>()
  const [document, setDocument] = useState<Document | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const loadDocument = async () => {
      if (!documentId) {
        setError('No document ID provided')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const doc = await getDocument(documentId)
        setDocument(doc)
      } catch (err: any) {
        setError(err.message || 'Failed to load document')
      } finally {
        setLoading(false)
      }
    }

    loadDocument()
  }, [documentId])

  const handleBackToList = () => {
    navigate('/')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading document...</p>
        </div>
      </div>
    )
  }

  if (error || !document) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <p className="font-medium">Failed to load document</p>
            <p className="text-sm">{error || 'Document not found'}</p>
          </div>
          <button
            onClick={handleBackToList}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Documents
          </button>
        </div>
      </div>
    )
  }

  return (
    <SmartAnalysisView
      document={document}
      onBackToList={handleBackToList}
    />
  )
}

// Students page component
const StudentsPage = () => {
  const [students, setStudents] = useState<Student[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  const loadStudents = async () => {
    try {
      setLoading(true)
      setError(null)
      const studentList = await getStudents()
      setStudents(studentList)
    } catch (error: any) {
      console.error('Failed to load students:', error)
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to load students. Please try again.'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadStudents()
  }, [])

  const handleSelectStudent = (student: Student) => {
    navigate(`/student/${student.id}`)
  }

  const handleCreateNew = () => {
    navigate('/students/create')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading students...</p>
        </div>
      </div>
    )
  }

  return (
    <StudentList
      students={students}
      error={error}
      onSelectStudent={handleSelectStudent}
      onCreateNew={handleCreateNew}
      onStudentUpdated={loadStudents}
      onRetry={loadStudents}
    />
  )
}

// Student detail page component
const StudentPage = () => {
  const { studentId } = useParams<{ studentId: string }>()
  const [student, setStudent] = useState<Student | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  const loadStudent = useCallback(async () => {
    if (!studentId) {
      setError('No student ID provided')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const studentData = await getStudent(studentId)
      setStudent(studentData)
    } catch (err: any) {
      setError(err.message || 'Failed to load student')
    } finally {
      setLoading(false)
    }
  }, [studentId])

  useEffect(() => {
    loadStudent()
  }, [studentId, loadStudent])

  const handleBack = () => {
    navigate('/students')
  }

  const handleSelectDocument = (document: Document) => {
    navigate(`/document/${document.id}`)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading student...</p>
        </div>
      </div>
    )
  }

  if (error || !student) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <p className="font-medium">Failed to load student</p>
            <p className="text-sm">{error || 'Student not found'}</p>
          </div>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Students
          </button>
        </div>
      </div>
    )
  }

  return (
    <StudentDetail
      student={student}
      onBack={handleBack}
      onStudentUpdated={loadStudent}
      onSelectDocument={handleSelectDocument}
    />
  )
}

// Student create page component
const StudentCreatePage = () => {
  const navigate = useNavigate()

  const handleBack = () => {
    navigate('/students')
  }

  const handleStudentCreated = () => {
    navigate('/students')
  }

  return (
    <StudentCreate
      onBack={handleBack}
      onStudentCreated={handleStudentCreated}
    />
  )
}

// Main app content (inside auth provider)
function AppContent() {
  const { user, loading } = useAuth()
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login')

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // Show landing page if not authenticated
  if (!user) {
    return (
      <>
        <LandingPage
          onShowLogin={() => {
            setAuthMode('login')
            setShowAuthModal(true)
          }}
          onShowSignup={() => {
            setAuthMode('signup')
            setShowAuthModal(true)
          }}
        />
        <AuthModal
          isOpen={showAuthModal}
          mode={authMode}
          onClose={() => setShowAuthModal(false)}
          onSwitchMode={() => setAuthMode(authMode === 'login' ? 'signup' : 'login')}
        />
      </>
    )
  }

  // Show authenticated app
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/upload" element={<UploadPage />} />
        <Route path="/document/:documentId" element={<DocumentPage />} />
        <Route path="/students" element={<StudentsPage />} />
        <Route path="/students/create" element={<StudentCreatePage />} />
        <Route path="/student/:studentId" element={<StudentPage />} />
      </Routes>
    </Layout>
  )
}

export function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}